import { useState } from "react";
import { COMPANY } from "@/lib/pricing";
import api from "@/lib/api";
import { toast } from "sonner";

const EMAIL_RE = /^\S+@\S+\.\S+$/;

// "Copy sales agent(s) (a@x.com, b@y.com)" label shared by the RFP and quote modals.
export const agentCcLabel = (agentEmails) => ({
  count: agentEmails.length,
  text: agentEmails.join(", "),
});

// Owns recipients + send options for the customer-facing quote email.
export function useQuoteEmail(rfpData) {
  const [emails, setEmails] = useState("");
  const [ccOwner, setCcOwner] = useState(false);
  const [ccAgent, setCcAgent] = useState(true);
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);

  // Main sales agents are CC'd along with any per-customer agents.
  const mainAgents = rfpData.salesAgents || (rfpData.salesAgent?.email ? [rfpData.salesAgent] : []);
  const agentEmails = [
    ...new Set(
      [
        ...mainAgents.map((a) => (a.email || "").trim()),
        ...(rfpData.customers || []).map((c) => (c.agent?.email || "").trim()),
      ].filter((e) => EMAIL_RE.test(e))
    ),
  ];
  const typedEmails = emails.split(",").map((s) => s.trim()).filter(Boolean);
  const recipients = [
    ...new Set([...typedEmails.filter((e) => EMAIL_RE.test(e)), ...(ccAgent ? agentEmails : [])]),
  ];

  const sendEmail = async (html) => {
    const invalid = typedEmails.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length) {
      toast.error(`Invalid email: ${invalid.join(", ")}`);
      return;
    }
    if (!recipients.length) {
      toast.error("Enter the customer or finance director email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/send-rfp", {
        recipient_emails: recipients,
        subject: `Equipment Delivery Quote — ${COMPANY.name}`,
        html_content: html,
        reply_to: COMPANY.rfpEmail,
        cc_owner: ccOwner,
        attach_pdf: attachPdf,
        base_url: window.location.origin,
        pdf_filename: "KG-Delivery-Quote.pdf",
      });
      toast.success(data.message || `Quote sent to ${recipients.length} recipient(s)`);
      setEmails("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send quote");
    } finally {
      setSending(false);
    }
  };

  return {
    emails,
    setEmails,
    ccOwner,
    setCcOwner,
    ccAgent,
    setCcAgent,
    attachPdf,
    setAttachPdf,
    sending,
    agentEmails,
    recipients,
    sendEmail,
  };
}
